import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Savings as SavingsIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Transaction, Category } from '../types';

// Enregistrement des composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface FinancialReportsProps {
  transactions: Transaction[];
  categories: Category[];
}

const FinancialReports: React.FC<FinancialReportsProps> = ({ transactions, categories }) => {
  const theme = useTheme();
  const [selectedPeriod, setSelectedPeriod] = useState<'3months' | '6months' | '1year' | 'all'>('6months');

  // Fonction utilitaire pour formater les montants
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Calcul des données filtrées par période
  const filteredTransactions = useMemo(() => {
    const now = new Date();
    let startDate = new Date();

    switch (selectedPeriod) {
      case '3months':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case '6months':
        startDate.setMonth(now.getMonth() - 6);
        break;
      case '1year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case 'all':
        return transactions;
    }

    return transactions.filter(t => new Date(t.date) >= startDate);
  }, [transactions, selectedPeriod]);

  // Calcul des données mensuelles
  const monthlyData = useMemo(() => {
    const data: { [key: string]: { income: number; expenses: number } } = {};

    filteredTransactions.forEach(transaction => {
      const date = new Date(transaction.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!data[monthKey]) {
        data[monthKey] = { income: 0, expenses: 0 };
      }

      if (transaction.type === 'income') {
        data[monthKey].income += transaction.amount;
      } else {
        data[monthKey].expenses += transaction.amount;
      }
    });

    return Object.entries(data)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, values]) => ({
        month: new Date(month + '-01').toLocaleDateString('fr-FR', { 
          month: 'short', 
          year: 'numeric' 
        }),
        income: values.income,
        expenses: values.expenses,
        balance: values.income - values.expenses,
      }));
  }, [filteredTransactions]);

  // Calcul des données par catégorie (dépenses uniquement)
  const categoryData = useMemo(() => {
    const expenseTransactions = filteredTransactions.filter(t => t.type === 'expense');
    const totalExpenses = expenseTransactions.reduce((sum, t) => sum + t.amount, 0);

    const categoryTotals: { [key: string]: number } = {};

    expenseTransactions.forEach(transaction => {
      const category = categories.find(c => c.name === transaction.category);
      if (category) {
        categoryTotals[category.name] = (categoryTotals[category.name] || 0) + transaction.amount;
      }
    });

    return Object.entries(categoryTotals)
      .map(([name, amount]) => {
        const category = categories.find(c => c.name === name);
        return {
          name,
          amount,
          color: category?.color || '#666',
          percentage: totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0,
        };
      })
      .sort((a, b) => b.amount - a.amount);
  }, [filteredTransactions, categories]);

  // Calcul des KPIs
  const kpis = useMemo(() => {
    const totalIncome = filteredTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);

    const totalExpenses = filteredTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);

    const balance = totalIncome - totalExpenses;
    const avgMonthlyIncome = monthlyData.length > 0 
      ? totalIncome / monthlyData.length 
      : 0;
    const avgMonthlyExpenses = monthlyData.length > 0 
      ? totalExpenses / monthlyData.length 
      : 0;

    return {
      totalIncome,
      totalExpenses,
      balance,
      avgMonthlyIncome,
      avgMonthlyExpenses,
      transactionCount: filteredTransactions.length,
    };
  }, [filteredTransactions, monthlyData]);

  // Configuration du graphique linéaire
  const lineChartData = {
    labels: monthlyData.map(d => d.month),
    datasets: [
      {
        label: 'Revenus',
        data: monthlyData.map(d => d.income),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Dépenses',
        data: monthlyData.map(d => d.expenses),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Évolution Revenus vs Dépenses',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return formatCurrency(value);
          },
        },
      },
    },
  };

  // Configuration du graphique en camembert
  const doughnutChartData = {
    labels: categoryData.map(d => d.name),
    datasets: [
      {
        data: categoryData.map(d => d.amount),
        backgroundColor: categoryData.map(d => d.color),
        borderWidth: 2,
        borderColor: theme.palette.background.paper,
      },
    ],
  };

  const doughnutChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: true,
        text: 'Répartition des Dépenses par Catégorie',
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const percentage = categoryData[context.dataIndex]?.percentage || 0;
            return `${context.label}: ${formatCurrency(context.raw)} (${percentage.toFixed(1)}%)`;
          },
        },
      },
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AnalyticsIcon fontSize="large" />
          📊 Rapports Financiers
        </Typography>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Période</InputLabel>
          <Select
            value={selectedPeriod}
            label="Période"
            onChange={(e) => setSelectedPeriod(e.target.value as any)}
          >
            <MenuItem value="3months">3 derniers mois</MenuItem>
            <MenuItem value="6months">6 derniers mois</MenuItem>
            <MenuItem value="1year">Dernière année</MenuItem>
            <MenuItem value="all">Toutes les données</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* KPIs */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
        gap: 3,
        mb: 4 
      }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TrendingUpIcon sx={{ color: 'success.main', mr: 1 }} />
              <Typography variant="h6" color="success.main">
                Revenus totaux
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {formatCurrency(kpis.totalIncome)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Moy. mensuelle: {formatCurrency(kpis.avgMonthlyIncome)}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TrendingDownIcon sx={{ color: 'error.main', mr: 1 }} />
              <Typography variant="h6" color="error.main">
                Dépenses totales
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {formatCurrency(kpis.totalExpenses)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Moy. mensuelle: {formatCurrency(kpis.avgMonthlyExpenses)}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalanceIcon sx={{ color: kpis.balance >= 0 ? 'success.main' : 'error.main', mr: 1 }} />
              <Typography variant="h6" color={kpis.balance >= 0 ? 'success.main' : 'error.main'}>
                Solde net
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold" color={kpis.balance >= 0 ? 'success.main' : 'error.main'}>
              {formatCurrency(kpis.balance)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {kpis.balance >= 0 ? '💰 Excédent' : '⚠️ Déficit'}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ReceiptIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" color="primary.main">
                Transactions
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {kpis.transactionCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sur la période sélectionnée
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Graphiques */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
        gap: 3,
        mb: 3
      }}>
        {/* Graphique linéaire */}
        <Card>
          <CardContent>
            <Line data={lineChartData} options={lineChartOptions} />
          </CardContent>
        </Card>

        {/* Graphique en camembert */}
        <Card>
          <CardContent>
            <Doughnut data={doughnutChartData} options={doughnutChartOptions} />
          </CardContent>
        </Card>
      </Box>

      {/* Détail des catégories */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SavingsIcon />
            Top Catégories de Dépenses
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2
          }}>
            {categoryData.slice(0, 6).map((category, index) => (
              <Paper key={index} sx={{ p: 2, border: `3px solid ${category.color}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {category.name}
                  </Typography>
                  <Chip 
                    label={`${category.percentage.toFixed(1)}%`}
                    size="small"
                    sx={{ backgroundColor: category.color, color: 'white' }}
                  />
                </Box>
                <Typography variant="h5" fontWeight="bold" color={category.color}>
                  {formatCurrency(category.amount)}
                </Typography>
              </Paper>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FinancialReports;